// src/pages/Payment/PlanosPage.jsx
// 📋 PropZyn - Página de Planos | Versão 5.0
// 🔥 V5: Trial de 7 dias integrado (startTrial) estilo Netflix/Spotify/Notion
// Single Source of Truth: src/config/plans.js
// Segurança: Preço calculado pelo backend, autenticação validada, sem fallbacks

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle, Shield, Clock, Star, Zap, ArrowRight,
  X, ChevronDown, Lock, Award, Headphones, RefreshCw,
  Home, Users, FileText, BarChart2, Building, AlertTriangle, Loader2,
  Sparkles, Gift
} from 'lucide-react';
import { useAuth } from '../../contexts/useAuth';
import { usePayment } from '../../contexts/PaymentContext/PaymentContext';
import { PLANS, LAUNCH_LIMIT, getPlansForDisplay, formatPrice, getPlanFeatures } from '../../config/plans';
import { useIsMobile } from '../../hooks/useMediaQuery';

/* ═══════════════════════════════════════════
   FAQ (MERCADO IMOBILIÁRIO)
═══════════════════════════════════════════ */
const FAQS = [
  {
    question: 'Posso cancelar quando quiser?',
    answer: 'Sim, sem multa. Cancele com um clique no painel. O acesso permanece até o final do período pago.',
  },
  {
    question: 'Como funciona o período de teste de 7 dias?',
    answer: 'Você usa todas as funcionalidades do plano escolhido por 7 dias sem pagar. Só cobramos após o teste terminar. Cancele antes e não paga nada.',
  },
  {
    question: 'Quais formas de pagamento são aceitas?',
    answer: 'Cartão de crédito (Visa, Mastercard, Amex, Elo), PIX e boleto bancário via Mercado Pago. Parcelamento em até 12x no cartão.',
  },
  {
    question: 'Posso mudar de plano depois?',
    answer: 'Sim! Upgrade é instantâneo (paga só a diferença). Downgrade ocorre no próximo ciclo de cobrança.',
  },
  {
    question: 'Tem desconto para imobiliárias com vários corretores?',
    answer: 'Sim! O plano Enterprise é ideal para equipes. Entre em contato para condições personalizadas.',
  },
  {
    question: 'Meus dados e os dos meus clientes estão seguros?',
    answer: 'Sim. Usamos criptografia AES-256, servidores no Brasil e seguimos a LGPD. Seus dados nunca são compartilhados.',
  },
];

/* ═══════════════════════════════════════════
   COMPONENTES AUXILIARES
═══════════════════════════════════════════ */

function CountUp({ end, duration = 2000, prefix = '', suffix = '' }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  useEffect(() => {
    if (!inView) return;
    let start = null;
    const tick = (timestamp) => {
      if (!start) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(end * eased));
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, end, duration]);

  return <span ref={ref}>{prefix}{count.toLocaleString('pt-BR')}{suffix}</span>;
}

function FaqItem({ question, answer, index }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.07 }}
      className="border border-slate-200 rounded-2xl overflow-hidden bg-white"
    >
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-6 py-4 text-left gap-4 hover:bg-slate-50 transition-colors"
      >
        <span className="font-semibold text-slate-900 text-sm md:text-base">{question}</span>
        <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }}>
          <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
        </motion.div>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <p className="px-6 pb-4 text-slate-600 text-sm leading-relaxed">{answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function PricingCard({ plan, period, isCurrent, onSelect, isSubmitting, submittingPlanId, index, isTrial }) {
  const isLaunch = plan.isLaunch;
  const monthlyPrice = plan.monthlyPrice / 100;
  const annualPrice = plan.annualPrice / 100;
  const monthlyEquivalent = plan.monthlyEquivalent / 100;
  const isEnterprise = plan.id === 'enterprise';
  const features = plan.features;
  const isThisSubmitting = submittingPlanId === plan.id;

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4, delay: index * 0.1 } },
  };

  return (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      whileHover={{ y: -4, transition: { duration: 0.2 } }}
      className={`
        relative flex flex-col rounded-3xl border bg-white
        ${plan.metadata.isPopular
          ? 'border-purple-400 ring-2 ring-purple-400 ring-offset-2 shadow-xl shadow-purple-100'
          : plan.metadata.isBestValue
          ? 'border-emerald-400 shadow-lg shadow-emerald-50'
          : 'border-slate-200 shadow-sm'}
      `}
    >
      {/* Badge de Lançamento */}
      {isLaunch && !isEnterprise && (
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold bg-amber-400 text-amber-900 whitespace-nowrap shadow-lg">
          🚀 LANÇAMENTO • {plan.launchRemaining} vagas
        </div>
      )}

      {/* Badge do plano */}
      {plan.metadata.badge && !isLaunch && (
        <div className={`
          absolute -top-3.5 left-1/2 -translate-x-1/2
          px-4 py-1 rounded-full text-xs font-bold tracking-wider whitespace-nowrap
          ${plan.metadata.isPopular ? 'bg-purple-600 text-white' : 'bg-emerald-600 text-white'}
        `}>
          {plan.metadata.badge}
        </div>
      )}

      <div className="p-6 md:p-8 flex flex-col flex-1">
        {/* Header */}
        <div className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-1">
            {plan.subtitle}
          </p>
          <h3 className="text-xl font-bold text-slate-900 mb-1 flex items-center gap-2">
            <span>{plan.metadata.icon}</span> {plan.name}
          </h3>
          {plan.metadata.highlight && !isLaunch && (
            <p className="text-xs font-medium text-purple-500">{plan.metadata.highlight}</p>
          )}
          {isLaunch && (
            <p className="text-xs font-bold text-amber-600">Preço de lançamento vitalício</p>
          )}
        </div>

        {/* Preço */}
        <div className="mb-6">
          {isEnterprise ? (
            <div>
              <p className="text-3xl font-bold text-slate-900">Sob consulta</p>
              <p className="text-sm text-slate-500 mt-1">Personalizado para sua imobiliária</p>
            </div>
          ) : (
            <div>
              {isLaunch && (
                <p className="text-sm line-through text-slate-400 mb-0.5">
                  De {formatPrice(plan.prices?.monthly?.regular || plan.monthlyPrice)}/mês
                </p>
              )}
              <div className="flex items-end gap-1">
                <span className="text-sm font-medium text-slate-500 mb-1">R$</span>
                <span className={`text-4xl font-extrabold tracking-tight ${
                  isLaunch ? 'text-amber-600' : 'text-slate-900'
                }`}>
                  {period === 'yearly'
                    ? monthlyEquivalent.toLocaleString('pt-BR')
                    : monthlyPrice.toLocaleString('pt-BR')}
                </span>
                <span className="text-sm text-slate-500 mb-1">/mês</span>
              </div>
              {period === 'yearly' && (
                <p className="text-xs text-emerald-600 font-semibold mt-1">
                  Economize R$ {((monthlyPrice * 12) - annualPrice).toLocaleString('pt-BR')} por ano
                </p>
              )}
              {isLaunch && (
                <p className="text-xs text-amber-600 font-semibold mt-1">
                  Apenas {plan.launchRemaining} vagas restantes
                </p>
              )}
            </div>
          )}
        </div>

        {/* 🔥 V5: CTA — Trial de 7 dias */}
        <motion.button
          onClick={() => onSelect(plan.id)}
          whileTap={{ scale: 0.97 }}
          disabled={isCurrent || isSubmitting}
          className={`
            w-full py-3.5 px-6 rounded-xl font-bold text-sm transition-all mb-3
            ${isCurrent || isSubmitting
              ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
              : isLaunch
              ? 'bg-amber-500 hover:bg-amber-600 text-white shadow-lg shadow-amber-200'
              : plan.metadata.isPopular
              ? 'bg-purple-600 hover:bg-purple-700 text-white shadow-lg shadow-purple-200'
              : plan.metadata.isBestValue
              ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-200'
              : 'bg-slate-900 hover:bg-slate-800 text-white'}
          `}
        >
          {isThisSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Ativando trial...
            </span>
          ) : isCurrent ? (
            isTrial ? (
              <span className="flex items-center justify-center gap-2">
                <Sparkles className="w-4 h-4" />
                Trial Ativo
              </span>
            ) : (
              '✓ Plano Atual'
            )
          ) : isEnterprise ? (
            'Falar com Consultor'
          ) : (
            <span className="flex items-center justify-center gap-2">
              <Gift className="w-4 h-4" />
              Começar 7 dias grátis
            </span>
          )}
          {!isCurrent && !isEnterprise && !isThisSubmitting && (
            <ArrowRight className="w-4 h-4 inline ml-1.5 -mt-0.5" />
          )}
        </motion.button>

        {!isEnterprise && (
          <p className="text-center text-xs text-emerald-600 font-semibold mb-5 flex items-center justify-center gap-1">
            <Clock className="w-3 h-3" />
            Sem compromisso • Cancele quando quiser
          </p>
        )}

        {/* Features */}
        <div className="flex-1 border-t border-slate-100 pt-5 space-y-3">
          {features.map((feat, i) => (
            <div key={i} className="flex items-start gap-2.5">
              {feat.included ? (
                <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-emerald-500" />
              ) : (
                <X className="w-4 h-4 flex-shrink-0 mt-0.5 text-slate-300" />
              )}
              <span className={`text-sm ${feat.included ? 'text-slate-700' : 'text-slate-400'}`}>
                {feat.text}
              </span>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

/* ═══════════════════════════════════════════
   COMPONENTE PRINCIPAL
═══════════════════════════════════════════ */
export default function PlanosPage() {
  const { user } = useAuth();
  const payment = usePayment();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [period, setPeriod] = useState('monthly');
  const [urgencyDismissed, setUrgencyDismissed] = useState(false);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [submittingPlan, setSubmittingPlan] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  // 🔥 V5: Banner de sucesso ao iniciar trial
  const [trialStarted, setTrialStarted] = useState(false);
  const [trialPlanName, setTrialPlanName] = useState('');

  // Buscar planos da API com preços dinâmicos
  useEffect(() => {
    async function fetchPlans() {
      try {
        const res = await fetch('/api/plans');
        const data = await res.json();
        if (data.success && data.plans) {
          setPlans(data.plans);
        }
      } catch (err) {
        console.warn('Usando planos locais:', err.message);
        setPlans(getPlansForDisplay({}));
      } finally {
        setLoading(false);
      }
    }
    fetchPlans();
  }, []);

  // Se pagamentos não está ativo, mostra mensagem
  if (!payment?.isEnabled) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center"
        >
          <div className="w-16 h-16 bg-purple-100 rounded-full mx-auto mb-4 flex items-center justify-center">
            <Home className="w-8 h-8 text-purple-600" />
          </div>
          <h1 className="text-2xl font-bold mb-3">Em breve!</h1>
          <p className="text-slate-600 mb-6">
            Nossos planos estarão disponíveis em breve. Por enquanto, todos os recursos estão liberados gratuitamente.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
          >
            Ir para o Dashboard
          </button>
        </motion.div>
      </div>
    );
  }

  // 🔥 V5: INICIAR TRIAL DE 7 DIAS
  const handleSelect = async (planId) => {
    const plan = plans.find(p => p.id === planId);
    if (!plan) return;

    // Enterprise → contato
    if (planId === 'enterprise') {
      window.location.href = 'mailto:contato@ilhaleads.com.br?subject=Plano%20Enterprise%20PropZyn';
      return;
    }

    // Validar autenticação
    if (!user?.uid || !user?.email) {
      setErrorMessage('Você precisa estar logado para iniciar o teste gratuito.');
      setTimeout(() => setErrorMessage(null), 5000);
      return;
    }

    // Prevenir doble click
    if (submittingPlan) return;

    setSubmittingPlan(planId);
    setErrorMessage(null);

    try {
      // 🔥 V5: Iniciar trial de 7 dias (sem checkout!)
      const trialData = payment.startTrial(planId);
      
      setTrialStarted(true);
      setTrialPlanName(plan.name);
      
      // Redirecionar ao dashboard após 2 segundos
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
      
    } catch (err) {
      console.error('Erro ao iniciar trial:', err);
      setErrorMessage('Erro ao ativar período de teste. Tente novamente.');
      setTimeout(() => setErrorMessage(null), 5000);
    } finally {
      setSubmittingPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F7FF]">

      {/* ── Barra de urgência ── */}
      <AnimatePresence>
        {!urgencyDismissed && !trialStarted && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="bg-amber-500 text-amber-900 text-center text-xs md:text-sm font-bold py-2.5 px-4 relative"
          >
            <span>🚀 Lançamento PropZyn • Preços especiais para os primeiros {LAUNCH_LIMIT} clientes de cada plano • </span>
            <span className="underline cursor-pointer" onClick={() => document.getElementById('pricing-grid')?.scrollIntoView({ behavior: 'smooth' })}>
              Garantir minha vaga
            </span>
            <button
              onClick={() => setUrgencyDismissed(true)}
              className="absolute right-4 top-1/2 -translate-y-1/2 opacity-60 hover:opacity-100"
            >
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 🔥 V5: BANNER DE TRIAL INICIADO (SUCESSO) */}
      <AnimatePresence>
        {trialStarted && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="bg-emerald-500 text-white text-center text-sm md:text-base font-bold py-3 px-4 relative"
          >
            <span className="flex items-center justify-center gap-2">
              <Sparkles className="w-4 h-4" />
              🎉 Trial de 7 dias do plano {trialPlanName} ativado! Redirecionando para o dashboard...
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ════════════════════════════════════════ */}
      {/* BANNER DE ERRO (ANIMADO) */}
      {/* ════════════════════════════════════════ */}
      <AnimatePresence>
        {errorMessage && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="bg-red-50 border-b border-red-200"
          >
            <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-red-700 text-sm">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                <span>{errorMessage}</span>
              </div>
              <button
                onClick={() => setErrorMessage(null)}
                className="text-red-400 hover:text-red-600 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Hero Section ── */}
      <div className="relative overflow-hidden bg-white border-b border-slate-100">
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, #7C3AED 1px, transparent 0)`,
            backgroundSize: '32px 32px',
          }}
        />

        <div className="relative max-w-5xl mx-auto px-4 pt-14 pb-10 md:pt-20 md:pb-14 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="inline-flex items-center gap-2 bg-purple-50 border border-purple-100 rounded-full px-4 py-1.5 mb-6"
          >
            <Zap className="w-4 h-4 text-purple-600" />
            <span className="text-xs font-semibold text-purple-700">
              Lançamento oficial • Preços especiais vitalícios
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-3xl md:text-5xl font-extrabold text-slate-900 leading-tight mb-4 tracking-tight"
          >
            Gerencie seus imóveis como
            <span className="relative inline-block ml-2">
              <span className="relative z-10 text-purple-600">um profissional</span>
              <svg className="absolute -bottom-1 left-0 w-full" height="6" viewBox="0 0 200 6" fill="none">
                <path d="M0 5 Q50 0 100 4 Q150 8 200 3" stroke="#7C3AED" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
              </svg>
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-base md:text-lg text-slate-500 max-w-xl mx-auto mb-8"
          >
            7 dias grátis em qualquer plano. Sem compromisso. Cancele quando quiser.
          </motion.p>

          {/* Toggle Mensal / Anual */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="inline-flex items-center gap-3 bg-slate-100 rounded-full p-1"
          >
            <button
              onClick={() => setPeriod('monthly')}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                period === 'monthly' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Mensal
            </button>
            <button
              onClick={() => setPeriod('yearly')}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all flex items-center gap-2 ${
                period === 'yearly' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Anual
              <span className="bg-emerald-100 text-emerald-700 text-xs font-bold px-2 py-0.5 rounded-full">
                2 meses grátis
              </span>
            </button>
          </motion.div>
        </div>
      </div>

      {/* ── Grid de Planos ── */}
      <div id="pricing-grid" className="max-w-5xl mx-auto px-4 py-14">
        {loading ? (
          <div className="text-center py-20">
            <RefreshCw className="w-8 h-8 text-purple-500 animate-spin mx-auto mb-4" />
            <p className="text-slate-500">Carregando planos...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-4xl mx-auto">
            {plans.map((plan, i) => (
              <PricingCard
                key={plan.id}
                plan={plan}
                period={period}
                isCurrent={payment.currentPlan === plan.id || payment.trialPlan === plan.id}
                isTrial={payment.isTrialActive && payment.trialPlan === plan.id}
                onSelect={handleSelect}
                isSubmitting={!!submittingPlan}
                submittingPlanId={submittingPlan}
                index={i}
              />
            ))}
          </div>
        )}

        {/* Trust badges */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-10 flex flex-wrap justify-center gap-x-8 gap-y-4"
        >
          {[
            { icon: <Shield className="w-4 h-4" />, text: 'Pagamento seguro via Mercado Pago' },
            { icon: <Lock className="w-4 h-4" />, text: 'Dados criptografados (LGPD)' },
            { icon: <RefreshCw className="w-4 h-4" />, text: 'Cancele quando quiser' },
            { icon: <Award className="w-4 h-4" />, text: '7 dias de garantia' },
            { icon: <Headphones className="w-4 h-4" />, text: 'Suporte em português' },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-2 text-sm text-slate-500">
              <span className="text-emerald-500">{item.icon}</span>
              {item.text}
            </div>
          ))}
        </motion.div>
      </div>

      {/* ── Comparativo de planos ── */}
      <div className="max-w-4xl mx-auto px-4 py-16">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 mb-3">
            Compare os planos
          </h2>
          <p className="text-slate-500 text-sm md:text-base">
            Escolha o plano ideal para o seu negócio imobiliário
          </p>
        </div>

        <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm">
          <div className="grid grid-cols-4 text-center text-sm font-bold border-b border-slate-100">
            <div className="py-4 px-3 text-left text-slate-500 text-xs font-semibold uppercase tracking-wider">
              Recurso
            </div>
            {plans.map(plan => (
              <div key={plan.id} className={`py-4 px-2 ${
                plan.metadata.isPopular ? 'bg-purple-50 text-purple-700' : 
                plan.metadata.isBestValue ? 'bg-emerald-50 text-emerald-700' : 'text-slate-700'
              }`}>
                {plan.name}
              </div>
            ))}
          </div>

          {[
            { label: 'Imóveis', key: 'imoveis' },
            { label: 'Clientes', key: 'clientes' },
            { label: 'Usuários', key: 'usuarios' },
            { label: 'Documentos', key: 'documentos' },
            { label: 'Suporte', key: 'suporte' },
            { label: 'API', key: 'api' },
            { label: 'White Label', key: 'whiteLabel' },
          ].map((row, ri) => (
            <div key={ri} className={`grid grid-cols-4 border-b border-slate-50 text-sm hover:bg-slate-50 transition-colors ${ri % 2 === 0 ? '' : 'bg-slate-50/50'}`}>
              <div className="py-3.5 px-4 text-slate-600 font-medium">{row.label}</div>
              {plans.map((plan, ci) => {
                const val = plan.limits?.[row.key];
                const isBool = typeof val === 'boolean';
                const isUnlimited = val === Infinity;
                const supportLabels = { email: 'Email', prioritario: 'Prioritário', dedicado: 'Dedicado 24/7' };
                
                return (
                  <div key={ci} className={`py-3.5 px-2 text-center ${
                    plan.metadata.isPopular ? 'bg-purple-50/50' : plan.metadata.isBestValue ? 'bg-emerald-50/50' : ''
                  }`}>
                    {isBool ? (
                      val ? <CheckCircle className="w-4 h-4 text-emerald-500 mx-auto" />
                          : <X className="w-4 h-4 text-slate-300 mx-auto" />
                    ) : (
                      <span className="text-slate-700 font-medium text-xs md:text-sm">
                        {isUnlimited ? 'Ilimitado' : row.key === 'suporte' ? supportLabels[val] || val : val}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}

          <div className="grid grid-cols-4 py-5 px-2 bg-slate-50">
            <div />
            {plans.map(plan => (
              <div key={plan.id} className="px-1">
                <button
                  onClick={() => handleSelect(plan.id)}
                  disabled={!!submittingPlan}
                  className={`w-full py-2.5 rounded-xl text-xs font-bold transition-all ${
                    submittingPlan
                      ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                      : plan.metadata.isPopular
                      ? 'bg-purple-600 hover:bg-purple-700 text-white'
                      : plan.metadata.isBestValue
                      ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                      : 'bg-white hover:bg-slate-100 text-slate-700 border border-slate-200'}
                  `}
                >
                  {plan.id === 'enterprise' ? 'Contato' : '7 dias grátis'}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Primeiros usuários ── */}
      <div className="bg-purple-600 py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-purple-200 text-sm font-semibold mb-2 uppercase tracking-widest">
              Early Adopters
            </p>
            <h2 className="text-2xl md:text-4xl font-extrabold text-white mb-4">
              Seja um dos primeiros
            </h2>
            <p className="text-purple-200 mb-8 text-sm md:text-base max-w-xl mx-auto">
              Os primeiros {LAUNCH_LIMIT} clientes de cada plano garantem preço de lançamento vitalício. 
              Comece agora com 50% de desconto para sempre.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-2xl mx-auto">
              {[
                { icon: <Zap className="w-6 h-6" />, title: 'Preço vitalício', desc: 'Seu preço nunca aumenta' },
                { icon: <Star className="w-6 h-6" />, title: 'Acesso antecipado', desc: 'Novas funcionalidades primeiro' },
                { icon: <Building className="w-6 h-6" />, title: 'Grupo VIP', desc: 'Canal direto com os fundadores' },
              ].map((item, i) => (
                <div key={i} className="bg-white/10 rounded-2xl p-6 backdrop-blur-sm">
                  <div className="text-amber-400 mb-3 flex justify-center">{item.icon}</div>
                  <h3 className="text-white font-bold mb-1">{item.title}</h3>
                  <p className="text-purple-200 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* ── FAQ ── */}
      <div className="max-w-2xl mx-auto px-4 py-16">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 mb-2">
            Perguntas frequentes
          </h2>
          <p className="text-slate-500 text-sm">Tudo que você precisa saber</p>
        </div>
        <div className="space-y-3">
          {FAQS.map((faq, i) => (
            <FaqItem key={i} {...faq} index={i} />
          ))}
        </div>
      </div>

      {/* ── CTA Final ── */}
      <div className="relative overflow-hidden bg-slate-900 py-16">
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
            backgroundSize: '28px 28px',
          }}
        />
        <div className="relative max-w-2xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <p className="text-slate-400 text-sm font-semibold mb-2 uppercase tracking-widest">
              Pronto para começar?
            </p>
            <h2 className="text-2xl md:text-4xl font-extrabold text-white mb-4">
              7 dias grátis, sem compromisso
            </h2>
            <p className="text-slate-400 mb-8 text-sm md:text-base">
              Comece agora e descubra como o PropZyn pode transformar sua gestão imobiliária.
            </p>
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={() => handleSelect('professional')}
              disabled={!!submittingPlan}
              className={`px-8 py-4 rounded-xl font-bold text-sm transition-colors shadow-lg shadow-purple-900/30 ${
                submittingPlan
                  ? 'bg-slate-600 text-slate-300 cursor-not-allowed'
                  : 'bg-purple-600 hover:bg-purple-700 text-white'
              }`}
            >
              {submittingPlan ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Processando...
                </span>
              ) : (
                <>
                  <Gift className="w-4 h-4" />
                  Começar 7 dias grátis
                  <ArrowRight className="w-4 h-4 inline ml-2 -mt-0.5" />
                </>
              )}
            </motion.button>
            <p className="text-slate-500 text-xs mt-5 flex items-center justify-center gap-1.5">
              <Lock className="w-3 h-3" />
              Pagamento seguro via Mercado Pago • Cancele quando quiser
            </p>
          </motion.div>
        </div>
      </div>

      {/* ── Status do plano atual ── */}
      {payment.currentPlan && (
        <div className="bg-slate-800 text-center py-3 px-4">
          <p className="text-sm text-slate-400">
            {payment.isTrialActive ? (
              <>
                <span className="flex items-center justify-center gap-2">
                  <Sparkles className="w-4 h-4 text-emerald-400" />
                  <span>Trial de 7 dias ativo • Plano </span>
                  <span className="font-bold text-white capitalize">{payment.trialPlan || payment.currentPlan}</span>
                  <span> • </span>
                  <span className="text-emerald-400 font-semibold">{payment.trialDaysRemaining} dias restantes</span>
                </span>
              </>
            ) : (
              <>
                Você está no plano{' '}
                <span className="font-bold text-white capitalize">{payment.currentPlan}</span>
                {' '}•{' '}
                <span className="text-emerald-400 font-semibold">Ativo</span>
              </>
            )}
          </p>
        </div>
      )}
    </div>
  );
}